import type { ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';
import { humanize } from '@/modules/simulator/ui/text';
import type { TicketBreakdownLevel, TicketBreakdownRow } from '@/types/analyticsV2';
import { TICKET_DRILLDOWNS, type TicketDrilldownKey } from '../../drilldown/ticket-drilldowns';

const LEVEL_LABEL: Record<TicketBreakdownLevel, string> = {
  category: 'Category',
  subcategory: 'Subcategory',
  intent: 'Intent',
};

/** The number columns of a breakdown row; each column id names the ticket list it opens. */
export const BREAKDOWN_NUMBERS = Object.keys(TICKET_DRILLDOWNS) as TicketDrilldownKey[];

export type OpenDrilldown = (row: TicketBreakdownRow, column: TicketDrilldownKey) => void;

/** A breakdown row's table id: its key, or '' for Uncategorized. */
export const breakdownRowId = ({ key }: TicketBreakdownRow): string => key ?? '';

export const breakdownRowName = (by: TicketBreakdownLevel, { key }: TicketBreakdownRow): string => {
  if (key === null) return 'Uncategorized';
  return by === 'intent' ? humanize(key) : key;
};

export const canDrill = (by: TicketBreakdownLevel, row: TicketBreakdownRow): boolean =>
  by !== 'intent' && row.key !== null;

const countPercent = ({ count, percent }: { count: number; percent: number | null }) => (
  <span className='inline-flex items-baseline gap-2'>
    <span>{count}</span>
    {percent !== null ? <span className='text-gray-700 text-[12px]'>{percent.toFixed(0)} %</span> : null}
  </span>
);

/** A number that opens the rows behind it, without opening the row's next level. It fills its cell, padding included. */
const opener = (content: ReactNode, label: string, open: () => void) => (
  <button
    type='button'
    aria-label={label}
    className='-mx-3 flex h-10 w-[calc(100%+1.5rem)] cursor-pointer items-center px-3 underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring'
    onClick={(event) => {
      event.stopPropagation();
      open();
    }}
  >
    {content}
  </button>
);

export const breakdownColumns = (by: TicketBreakdownLevel, onOpen: OpenDrilldown): ColumnDef<TicketBreakdownRow>[] => {
  const number = (row: TicketBreakdownRow, content: ReactNode, column: TicketDrilldownKey) =>
    opener(content, `${TICKET_DRILLDOWNS[column].title} in ${breakdownRowName(by, row)}`, () => onOpen(row, column));
  return [
    {
      id: 'name',
      header: LEVEL_LABEL[by],
      size: 260,
      cell: ({ row }) => {
        const name = (
          <>
            <div className='h-4 w-[2px] rounded-full bg-gray-500' />
            <span className='text-ink truncate'>{breakdownRowName(by, row.original)}</span>
          </>
        );
        return canDrill(by, row.original) ? (
          <button type='button' className='flex max-w-full items-center gap-2 text-left'>
            {name}
            <ChevronRight className='size-3.5 shrink-0 text-gray-500' />
          </button>
        ) : (
          <div className='flex items-center gap-2'>{name}</div>
        );
      },
    },
    {
      id: 'tickets',
      header: 'Tickets',
      size: 110,
      cell: ({ row }) => number(row.original, row.original.tickets, 'tickets'),
    },
    {
      id: 'aiHandled',
      header: 'AI handled',
      size: 140,
      cell: ({ row }) => number(row.original, countPercent(row.original.aiHandled), 'aiHandled'),
    },
    {
      id: 'automated',
      header: 'Automated',
      size: 140,
      cell: ({ row }) => number(row.original, countPercent(row.original.automated), 'automated'),
    },
    {
      id: 'avgAiResponses',
      header: 'AI responses /ticket',
      size: 160,
      cell: ({ row }) =>
        row.original.avgAiResponses === null
          ? '—'
          : number(row.original, row.original.avgAiResponses.toFixed(2), 'avgAiResponses'),
    },
  ];
};
